import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, BatteryCharging, Leaf, Sparkles, ArrowRight, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { whatsappLinks } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";

const HIGHLIGHTS = [
  { icon: ShieldCheck, title: "Geprüft & zertifiziert", text: "Jedes Gerät durchläuft einen 40-Punkte-Check in unserer Werkstatt." },
  { icon: BatteryCharging, title: "Neuer Akku", text: "Neue oder ≥ 95% Akku-Kapazität — wir tauschen, was nicht passt." },
  { icon: Leaf, title: "Nachhaltig", text: "Bis zu 70% weniger CO₂ als ein neues Gerät — gut für dich und den Planeten." },
  { icon: Sparkles, title: "Bis zu 50% günstiger", text: "Top-Preise als faire Orientierung. Live-Verfügbarkeit direkt im Shop." },
];

export default function RefurbishedPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearFilter, setYearFilter] = useState("all"); // all | 2025 | 2024 | 2023 | 2022 | 2021 | older

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get("/refurbished/iphones")
      .then((r) => {
        if (alive) setItems(r.data || []);
      })
      .catch((e) => {
        if (alive) setError(e?.message || "Konnte Modelle nicht laden.");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const yearOptions = useMemo(() => {
    const years = Array.from(new Set(items.map((i) => i.year))).sort((a, b) => b - a);
    return years;
  }, [items]);

  const filtered = useMemo(() => {
    if (yearFilter === "all") return items;
    if (yearFilter === "older") return items.filter((i) => i.year <= 2020);
    return items.filter((i) => String(i.year) === String(yearFilter));
  }, [items, yearFilter]);

  return (
    <div className="pt-32 pb-24 bg-[#0A0A0B] min-h-screen relative" data-testid="refurbished-page">
      {/* Background glow */}
      <div className="absolute inset-x-0 top-0 h-[520px] pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-emerald-500/8 blur-[160px]"></div>
        <div className="absolute inset-0 dot-bg opacity-40"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        {/* HERO */}
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-8"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 backdrop-blur">
              <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-[10px] uppercase tracking-[0.24em] text-emerald-200 font-bold">Refurbished iPhones</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[80px] font-bold tracking-tight text-white mt-5 leading-[1.0]">
              Wie&nbsp;neu.<br />
              <span className="bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-300 bg-clip-text text-transparent">
                Halb&nbsp;so&nbsp;teuer.
              </span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl">
              Professionell aufbereitete iPhones — geprüft, gereinigt und mit frischem Akku.{" "}
              <span className="text-white font-medium">Wir beraten dich persönlich</span> zur aktuellen
              Verfügbarkeit im Geschäft.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={whatsappLinks.refurbished({ model: "iPhone", storage: "", price: "" }).href}
                onClick={whatsappLinks.refurbished({ model: "iPhone", storage: "", price: "" }).onClick}
                target="_blank"
                rel="noreferrer"
                data-testid="refurb-hero-whatsapp-cta"
              >
                <Button className="bg-emerald-500 hover:bg-emerald-400 text-[#0A0A0B] rounded-full h-12 px-7 text-base font-semibold btn-lift">
                  Verfügbarkeit per WhatsApp anfragen
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </a>
              <a href="tel:+436764440304" data-testid="refurb-hero-call-cta">
                <Button
                  variant="outline"
                  className="rounded-full h-12 px-7 text-base bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white btn-lift"
                >
                  +43 676 4440304
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Highlights stack */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-3"
          >
            {HIGHLIGHTS.map((h, i) => (
              <div
                key={i}
                className="p-4 md:p-5 rounded-2xl border border-white/8 bg-white/[0.03] backdrop-blur"
                data-testid={`refurb-highlight-${i}`}
              >
                <h.icon className="w-5 h-5 text-emerald-300" />
                <div className="font-display text-sm md:text-base font-semibold text-white mt-2">{h.title}</div>
                <div className="text-xs text-zinc-500 mt-1 leading-relaxed hidden md:block">{h.text}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* DIVIDER */}
        <div className="mt-20 mb-10 flex items-center gap-4">
          <div className="text-[10px] uppercase tracking-[0.32em] text-zinc-500 font-bold">Aktuelle Modelle</div>
          <div className="flex-1 h-px bg-gradient-to-r from-white/10 to-transparent"></div>
        </div>

        {/* FILTER BAR */}
        <div className="mb-8 flex flex-wrap items-center gap-2" data-testid="refurb-filter-bar">
          <div className="inline-flex items-center gap-1.5 text-xs text-zinc-500 mr-2 uppercase tracking-wider">
            <Filter className="w-3.5 h-3.5" /> Jahrgang
          </div>
          <FilterChip active={yearFilter === "all"} onClick={() => setYearFilter("all")} testid="refurb-filter-all">
            Alle
          </FilterChip>
          {yearOptions
            .filter((y) => y >= 2021)
            .map((y) => (
              <FilterChip
                key={y}
                active={String(yearFilter) === String(y)}
                onClick={() => setYearFilter(String(y))}
                testid={`refurb-filter-${y}`}
              >
                {y}
              </FilterChip>
            ))}
          {yearOptions.some((y) => y <= 2020) && (
            <FilterChip
              active={yearFilter === "older"}
              onClick={() => setYearFilter("older")}
              testid="refurb-filter-older"
            >
              2020 & älter
            </FilterChip>
          )}
        </div>

        {/* GRID */}
        {loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="refurb-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-64 rounded-3xl bg-white/[0.03] border border-white/5 animate-pulse"></div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="p-6 rounded-2xl border border-red-500/30 bg-red-500/5 text-red-300 text-sm" data-testid="refurb-error">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="refurb-grid">
            {filtered.map((it, idx) => (
              <RefurbishedCard key={it.model} item={it} idx={idx} />
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full p-10 rounded-2xl border border-white/5 bg-white/[0.02] text-center text-zinc-500" data-testid="refurb-empty">
                Keine Modelle für diesen Jahrgang gefunden.
              </div>
            )}
          </div>
        )}

        {/* FOOTNOTE */}
        <div className="mt-16 p-6 md:p-8 rounded-3xl border border-white/8 bg-gradient-to-br from-[#13130F] via-[#0E0E0B] to-[#0A0A0B]" data-testid="refurb-footnote">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-300 font-bold">Hinweis</div>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed max-w-3xl">
                Tagesaktuelle Verfügbarkeit, Zustand (Gut / Sehr gut / Wie neu) und Endpreis erhältst du direkt
                von uns — persönlich, per Telefon oder WhatsApp.
              </p>
            </div>
            <a
              href={whatsappLinks.refurbished({ model: "iPhone", storage: "", price: "" }).href}
              onClick={whatsappLinks.refurbished({ model: "iPhone", storage: "", price: "" }).onClick}
              target="_blank"
              rel="noreferrer"
              data-testid="refurb-footnote-cta"
            >
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-[#0A0A0B] rounded-full h-11 px-6 font-semibold btn-lift">
                Jetzt anfragen <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterChip({ children, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3.5 h-9 rounded-full text-xs font-medium transition-all border ${
        active
          ? "bg-emerald-500 border-emerald-400 text-[#0A0A0B]"
          : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10 hover:border-white/20"
      }`}
    >
      {children}
    </button>
  );
}

function RefurbishedCard({ item, idx }) {
  const [selected, setSelected] = useState(item.storages?.[0]);
  const wa = whatsappLinks.refurbished({
    model: item.model,
    storage: selected?.label,
    price: selected?.price,
  });

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: Math.min(idx * 0.04, 0.4) }}
      className="group relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-[#15151A] via-[#101013] to-[#0A0A0B] p-6 flex flex-col"
      data-testid={`refurb-card-${item.model.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full bg-emerald-500/8 blur-[80px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

      <div className="relative flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Apple · {item.year}</div>
          <h3 className="font-display text-xl font-bold text-white mt-1.5 leading-tight">{item.model}</h3>
        </div>
        <div className="px-2.5 py-1 rounded-full bg-emerald-500/12 border border-emerald-500/25 text-[10px] uppercase tracking-wider text-emerald-300 font-bold">
          Refurb.
        </div>
      </div>

      <div className="relative mt-6 flex items-baseline gap-2">
        <span className="font-display text-3xl font-bold text-white numeric">€ {selected?.price ?? "—"}</span>
        <span className="text-xs text-zinc-500">ab</span>
      </div>
      <div className="text-[11px] text-zinc-500 mt-0.5">Richtpreis · {selected?.label}</div>

      <div className="relative mt-5 flex flex-wrap gap-1.5" data-testid="refurb-storage-options">
        {item.storages?.map((s) => (
          <button
            key={s.label}
            onClick={() => setSelected(s)}
            data-testid={`refurb-storage-${s.label.replace(/\s+/g, "")}`}
            className={`px-2.5 h-8 rounded-full text-[11px] font-medium border transition-all ${
              selected?.label === s.label
                ? "bg-white text-[#0A0A0B] border-white"
                : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <a
        href={wa.href}
        onClick={wa.onClick}
        target="_blank"
        rel="noreferrer"
        data-testid="refurb-card-cta"
        className="relative mt-6 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0A0B] font-semibold text-sm btn-lift"
      >
        Per WhatsApp anfragen
        <ArrowRight className="w-4 h-4" />
      </a>
    </motion.article>
  );
}
